import { useForm, useFieldArray, Controller } from "react-hook-form";
import {
  Button,
  Stack,
  Card,
  Title,
  Group,
  Divider,
  Select,
  Checkbox,
  Text,
} from "@mantine/core";
import { TextInput } from "react-hook-form-mantine";

import resolver from "@congrs/resolver";
import { web_createRegistrationCustomFieldSchema } from "@congrs/schemas";
import { useCreateRegistrationCustomField } from "../../api/registrationCustomFields";
import { makeI18nDefaultValue } from "../../utils/i18nUtils";
import LocalizedInput from "../common/LocalizedInput";

function CreateCustomField({ event }) {
  const createField = useCreateRegistrationCustomField();

  const mainLanguage = event?.language || "en";
  const otherLanguages = event?.secondaryLanguages || [];

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty, errors },
    reset,
  } = useForm({
    defaultValues: {
      eventId: event?.id,
      labelI18n: makeI18nDefaultValue(mainLanguage, otherLanguages),
      key: "",
      type: "text",
      required: false,
      options: [],
      appliedTo: "registrations",
      showOnRegistrationForm: true,
    },
    resolver: resolver(web_createRegistrationCustomFieldSchema),
  });

  const selectedType = watch("type");
  const showOnRegistrationForm = watch("showOnRegistrationForm");
  const { fields, append, remove } = useFieldArray({
    control,
    name: "options",
  });

  const onSubmit = (values) => {
    const payload = {
      ...values,
      required: values.showOnRegistrationForm ? values.required : false,
    };

    createField.mutate(payload, {
      onSuccess: () =>
        reset({
          eventId: event?.id,
          labelI18n: makeI18nDefaultValue(mainLanguage, otherLanguages),
          key: "",
          type: "text",
          required: false,
          options: [],
          appliedTo: "registrations",
          showOnRegistrationForm: true,
        }),
    });
  };

  const localizedInputFields = [
    {
      nameBase: "labelI18n",
      label: "Label",
      component: TextInput,
      props: {
        size: "sm",
        placeholder: "Label",
        withAsterisk: true,
      },
    },
  ];

  return (
    <Card
      shadow="sm"
      p="lg"
      radius="md"
      mt="sm"
      withBorder
      style={{ maxWidth: 600 }}
    >
      <Title order={3}>Create Custom Field</Title>
      <Divider my="md" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="md">
          <LocalizedInput
            control={control}
            fields={localizedInputFields}
            mainLanguage={mainLanguage}
            otherLanguages={otherLanguages}
          />

          <TextInput
            control={control}
            name="key"
            label="Key"
            placeholder="unique_key"
          />

          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <Select
                label="Type"
                data={[
                  { value: "text", label: "Text" },
                  { value: "boolean", label: "Boolean" },
                  { value: "select", label: "Select" },
                  { value: "multiselect", label: "Multiselect" },
                  { value: "date", label: "Date" },
                  { value: "country", label: "Country" },
                ]}
                {...field}
                error={errors.type?.message}
                allowDeselect={false}
              />
            )}
          />

          <Controller
            name="appliedTo"
            control={control}
            render={({ field }) => (
              <Select
                label="Applied To"
                data={[
                  { value: "registrations", label: "Registrations" },
                  { value: "ticket", label: "Ticket" },
                ]}
                {...field}
                error={errors.appliedTo?.message}
                allowDeselect={false}
              />
            )}
          />

          <Controller
            name="showOnRegistrationForm"
            control={control}
            render={({ field }) => (
              <Checkbox
                label="Show on registration form"
                checked={field.value}
                onChange={(e) => field.onChange(e.currentTarget.checked)}
              />
            )}
          />

          {showOnRegistrationForm && (
            <Controller
              name="required"
              control={control}
              render={({ field }) => (
                <Checkbox
                  label="Required"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.currentTarget.checked)}
                />
              )}
            />
          )}

          {(selectedType === "select" || selectedType === "multiselect") && (
            <Stack>
              <Divider label="Options" />
              {fields.map((field, index) => (
                <Card key={field.id} withBorder p="sm" radius="sm">
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={500}>
                      Option {index + 1}
                    </Text>
                    <Button
                      color="red"
                      variant="light"
                      size="xs"
                      onClick={() => remove(index)}
                    >
                      X
                    </Button>
                  </Group>
                  <LocalizedInput
                    control={control}
                    fields={[
                      {
                        nameBase: `options.${index}.nameI18n`,
                        label: "Name",
                        component: TextInput,
                        props: {
                          size: "sm",
                          placeholder: `Option ${index + 1}`,
                          withAsterisk: true,
                        },
                      },
                    ]}
                    mainLanguage={mainLanguage}
                    otherLanguages={otherLanguages}
                  />
                </Card>
              ))}
              <Button
                variant="light"
                onClick={() =>
                  append({
                    nameI18n: makeI18nDefaultValue(
                      mainLanguage,
                      otherLanguages
                    ),
                  })
                }
                style={{ width: 140 }}
              >
                Add option
              </Button>
            </Stack>
          )}

          <Group justify="flex-end" mt="md">
            <Button
              type="submit"
              loading={createField.isPending}
              disabled={!isDirty}
            >
              Create
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
}

export default CreateCustomField;
